import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle2, AlertCircle, Upload } from 'lucide-react';
import MemberLayout from '../../components/MemberLayout';
import { Field, Spinner, fmtDate } from '../../components/ui';
import api, { errMsg } from '../../services/api';
import { useGroup, useMember } from '../../context/AuthContext';

const COUNTRIES = ['Sri Lanka', 'Australia', 'United Kingdom', 'United States', 'Canada', 'New Zealand', 'United Arab Emirates', 'Qatar', 'Singapore', 'Japan', 'Germany', 'Maldives', 'India'];

const EMPTY = {
  fullName: '', nameWithInitials: '', className: '', hasSriLankanNic: true, nicNumber: '', dateOfBirth: '',
  email: '', phone: '', rcuCardNumber: '', rcuRegisteredDate: '', city: '', country: 'Sri Lanka', profession: '',
  password: '', confirmPassword: '', consent: false,
};

function Section({ n, title, children }) {
  return (
    <section className="card p-5 md:p-6">
      <h2 className="mb-4 flex items-center gap-3 text-lg font-semibold text-navy-900">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-navy-900 text-sm text-gold-400">{n}</span>{title}
      </h2>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

export default function Register() {
  const { group } = useGroup();
  const { signIn } = useMember();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [card, setCard] = useState(null);
  const [cardPreview, setCardPreview] = useState(null);
  const [nicInfo, setNicInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Read date of birth from the NIC as the member types
  useEffect(() => {
    const nic = form.nicNumber.trim();
    if (!form.hasSriLankanNic || nic.length < 10) { setNicInfo(null); return; }
    const t = setTimeout(() => api.post('/public/nic', { nic }).then(r => setNicInfo(r.data)).catch(() => {}), 350);
    return () => clearTimeout(t);
  }, [form.nicNumber, form.hasSriLankanNic]);

  useEffect(() => {
    if (!card || !card.type.startsWith('image/')) { setCardPreview(null); return; }
    const url = URL.createObjectURL(card);
    setCardPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [card]);

  const submit = async e => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) return toast.error('Passwords do not match');
    if (form.hasSriLankanNic && nicInfo && !nicInfo.valid) return toast.error(nicInfo.error);
    if (!card) return toast.error('Please upload a photo of your RCU membership card');
    if (!form.consent) return toast.error('Please accept the privacy & consent statement');

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => { if (k !== 'confirmPassword') fd.append(k, v); });
    fd.append('rcuCardImage', card);
    setBusy(true);
    try {
      const { data } = await api.post('/public/register', fd);
      signIn(data.token, data.member);
      toast.success('Registered! Please verify your email.');
      navigate('/verify-email');
    } catch (err) {
      toast.error(errMsg(err, 'Registration failed'));
    } finally {
      setBusy(false);
    }
  };

  const classes = group?.classes || [];

  return (
    <MemberLayout>
      <div className="mx-auto max-w-3xl">
        <div className="mb-6 text-center">
          <h1 className="font-display text-4xl font-bold text-navy-900">Member registration</h1>
          <p className="mt-2 text-gray-600">Takes about two minutes. Already registered? <Link to="/login" className="font-medium text-navy-700 underline">Log in</Link></p>
        </div>

        <form onSubmit={submit} className="space-y-5">
          <Section n={1} title="About you">
            <Field label="Full name" required className="sm:col-span-2">
              <input className="input" required value={form.fullName} onChange={e => set('fullName', e.target.value)} autoComplete="name" />
            </Field>
            <Field label="Name with initials">
              <input className="input" value={form.nameWithInitials} onChange={e => set('nameWithInitials', e.target.value)} placeholder="e.g. A. B. Perera" />
            </Field>
            <Field label="Class" required>
              {classes.length ? (
                <select className="input" required value={form.className} onChange={e => set('className', e.target.value)}>
                  <option value="">Select class</option>
                  {classes.map(c => <option key={c}>{c}</option>)}
                </select>
              ) : (
                <input className="input" required value={form.className} onChange={e => set('className', e.target.value)} placeholder="e.g. 11-A" />
              )}
            </Field>

            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" className="h-4 w-4 accent-navy-900" checked={!form.hasSriLankanNic} onChange={e => set('hasSriLankanNic', !e.target.checked)} />
                I don't have a Sri Lankan NIC
              </label>
            </div>
            {form.hasSriLankanNic ? (
              <Field label="NIC number" required hint="Old (e.g. 921234567V) or new (12 digits) format. Your date of birth is read from it.">
                <input className="input uppercase" required value={form.nicNumber} onChange={e => set('nicNumber', e.target.value)} maxLength={12} />
                {nicInfo && (nicInfo.valid
                  ? <p className="mt-1.5 flex items-center gap-1 text-xs text-green-700"><CheckCircle2 size={14} /> Date of birth: {fmtDate(nicInfo.dateOfBirth)}</p>
                  : <p className="mt-1.5 flex items-center gap-1 text-xs text-red-600"><AlertCircle size={14} /> {nicInfo.error}</p>)}
              </Field>
            ) : (
              <Field label="Date of birth" required>
                <input type="date" className="input" required value={form.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} max={new Date().toISOString().slice(0, 10)} />
              </Field>
            )}
          </Section>

          <Section n={2} title="Contact details">
            <Field label="Email" required hint="We'll send a verification link to this address.">
              <input type="email" className="input" required value={form.email} onChange={e => set('email', e.target.value)} autoComplete="email" />
            </Field>
            <Field label="Mobile number" required hint="e.g. 0771234567 or +61 4xx xxx xxx">
              <input type="tel" className="input" required value={form.phone} onChange={e => set('phone', e.target.value)} autoComplete="tel" />
            </Field>
            <Field label="City">
              <input className="input" value={form.city} onChange={e => set('city', e.target.value)} />
            </Field>
            <Field label="Country">
              <input className="input" list="countries" value={form.country} onChange={e => set('country', e.target.value)} />
              <datalist id="countries">{COUNTRIES.map(c => <option key={c} value={c} />)}</datalist>
            </Field>
            <Field label="Profession" className="sm:col-span-2">
              <input className="input" value={form.profession} onChange={e => set('profession', e.target.value)} placeholder="Optional" />
            </Field>
          </Section>

          <Section n={3} title="RCU membership">
            <Field label="RCU card number" required>
              <input className="input" required value={form.rcuCardNumber} onChange={e => set('rcuCardNumber', e.target.value)} />
            </Field>
            <Field label="Registered date" required hint="As shown on your card">
              <input type="date" className="input" required value={form.rcuRegisteredDate} onChange={e => set('rcuRegisteredDate', e.target.value)} max={new Date().toISOString().slice(0, 10)} />
            </Field>
            <Field label="Photo of your RCU card" required className="sm:col-span-2" hint="JPG, PNG or PDF, up to 8 MB. Only appointed admins can see it.">
              <label className="flex cursor-pointer items-center gap-4 rounded-lg border-2 border-dashed border-gray-300 p-4 hover:border-navy-400">
                {cardPreview
                  ? <img src={cardPreview} alt="RCU card" className="h-20 w-32 rounded object-cover" />
                  : <span className="flex h-20 w-32 items-center justify-center rounded bg-gray-100 text-gray-400"><Upload size={22} /></span>}
                <span className="text-sm text-gray-600">{card ? card.name : 'Click to choose a photo of your card'}</span>
                <input type="file" accept="image/*,.pdf" className="hidden" onChange={e => setCard(e.target.files[0] || null)} />
              </label>
            </Field>
          </Section>

          <Section n={4} title="Password & consent">
            <Field label="Password" required hint="At least 8 characters">
              <input type="password" className="input" required minLength={8} value={form.password} onChange={e => set('password', e.target.value)} autoComplete="new-password" />
            </Field>
            <Field label="Confirm password" required>
              <input type="password" className="input" required value={form.confirmPassword} onChange={e => set('confirmPassword', e.target.value)} autoComplete="new-password" />
            </Field>
            <label className="flex items-start gap-3 rounded-lg bg-navy-50 p-4 text-sm text-gray-700 sm:col-span-2">
              <input type="checkbox" className="mt-0.5 h-4 w-4 flex-shrink-0 accent-navy-900" checked={form.consent} onChange={e => set('consent', e.target.checked)} />
              <span>
                I agree that {group?.name || 'the group'} may store my details and use them only for group communication. My NIC number and RCU card are visible only to appointed admins, are never shared or sold, and I can unsubscribe or ask for my data to be deleted at any time.{' '}
                <Link to="/privacy" target="_blank" className="text-navy-700 underline">Read more</Link>
              </span>
            </label>
          </Section>

          <button type="submit" disabled={busy} className="btn-primary w-full py-3 text-base">
            {busy && <Spinner size={18} />} Register
          </button>
        </form>
      </div>
    </MemberLayout>
  );
}
